import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Panel } from '../components/ui/Panel';
import { StatusBadge } from '../components/ui/Badge';
import { Sparkline } from '../components/ui/charts/Sparkline';
import { Plus, Link2, Copy, CheckCircle, Search, Pencil, Trash2, ChevronDown, ChevronUp, DollarSign, Info, AlertTriangle } from 'lucide-react';
import { copyToClipboard, formatPercent } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { SourceCostDrawer } from '../components/sources/SourceCostDrawer';

interface PublisherRow {
  id: number;
  subId: string;
  displayName?: string | null;
  payoutType: 'cpa' | 'cpl' | 'revshare' | 'flat';
  payoutValue: number;
  currency: string;
  isActive: boolean;
  campaignIds?: number[];
  qualityScore?: number;
  costModel?: 'none' | 'cpc' | 'cpm' | 'flat';
  costValue?: number;
}

interface PublisherStats {
  publisherId: number | null;
  conversionsApproved: number;
  conversionsPending: number;
  conversionsRejected: number;
  conversionsReversed: number;
  revenueGross: number;
  payoutGross: number;
  clicksTotal: number;
  clicksQuality: number;
  clicksBot: number;
  cost?: number;
  profit?: number;
  roiPct?: number | null;
}

interface BySubIdRow {
  subId: string;
  linkedPublisherId: number | null;
  linkedName: string | null;
  clicksTotal: number;
  clicksBot: number;
  botRatePct: number;
  conversionsApproved: number;
  conversionsPending: number;
  revenueGross: number;
  cr: number;
  epc: number;
}

interface TrackingLink {
  key: string;
  domain: string;
  isDefault: boolean;
  campaignName: string;
  campaignSlug: string;
  url: string;
}

export function PublishersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editFor, setEditFor] = useState<PublisherRow & { stats?: PublisherStats } | null>(null);
  const [linksFor, setLinksFor] = useState<{ id: number; subId: string; campaignIds?: number[] } | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [costTarget, setCostTarget] = useState<{
    publisher: PublisherRow;
    clicks: number;
    revenue: number;
    profitBeforeCost: number;
  } | null>(null);

  // Fetch publishers list
  const list = useQuery({
    queryKey: ['publishers'],
    queryFn: () => apiClient.get<PublisherRow[]>('/api/v1/publishers'),
  });

  // Fetch 30D stats by publisher (for CONV/REVENUE per source)
  const stats30d = useQuery({
    queryKey: ['stats-by-publisher', '30d'],
    queryFn: () => apiClient.get<PublisherStats[]>('/api/v1/stats/by-publisher?range=30d'),
    staleTime: 60_000, // 1 min
  });

  // Create a lookup map: publisherId → stats row
  const statsMap = new Map((stats30d.data ?? []).map((s) => [s.publisherId!, s]));

  // Fetch by-subid data for Quality Snapshot (bot rates)
  const bySubId = useQuery({
    queryKey: ['stats-by-subid', '30d', 'min_clicks=10'],
    queryFn: () => apiClient.get<BySubIdRow[]>('/api/v1/stats/by-subid?range=30d&min_clicks=10&limit=200'),
    staleTime: 60_000,
  });

  // Create a lookup map: subId → bySubId row
  const bySubIdMap = new Map(bySubId.data?.map((r) => [r.subId, r]) ?? []);

  // Merge publishers with stats and by-subid quality data
  const mergedData = (list.data ?? []).map((p) => {
    const stats = statsMap.get(p.id);
    const quality = bySubIdMap.get(p.subId);
    return {
      ...p,
      stats: stats ?? {
        publisherId: p.id,
        conversionsApproved: 0,
        conversionsPending: 0,
        conversionsRejected: 0,
        conversionsReversed: 0,
        revenueGross: 0,
        payoutGross: 0,
        clicksTotal: 0,
        clicksQuality: 0,
        clicksBot: 0,
      },
      quality,
    };
  });

  const create = useMutation({
    mutationFn: (d: any) => apiClient.post('/api/v1/publishers', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publishers'] });
      qc.invalidateQueries({ queryKey: ['stats-by-publisher'] });
      setOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.patch(`/api/v1/publishers/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publishers'] });
      qc.invalidateQueries({ queryKey: ['stats-by-publisher'] });
      setEditFor(null);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/publishers/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['publishers'] });
      qc.invalidateQueries({ queryKey: ['stats-by-publisher'] });
    },
  });

  const handleArchiveOrDelete = async (pub: { id: number; subId: string }) => {
    const result = await confirm({
      title: `Delete ${pub.subId}?`,
      description: 'Permanently removes this traffic source. Past clicks/conversions keep their publisher_id but lose the sub_id.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') archive.mutate({ id: pub.id, hard: true });
  };

  const EPC = (revenue: number, clicks: number) => {
    if (clicks > 0) {
      const val = revenue / clicks;
      return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return '—';
  };

  // KPI strip 3 kartu (Stitch layar 6): sumber aktif, rata-rata CR, revenue bulanan.
  // CR memakai clicks quality 30D (klik non-bot) — sama dengan definisi CR di Statistics.
  const activeSources = mergedData.filter((p) => p.isActive).length;
  const totals30d = (stats30d.data ?? []).reduce(
    (acc, s) => ({
      clicksQuality: acc.clicksQuality + (s.clicksQuality ?? 0),
      conversions: acc.conversions + (s.conversionsApproved ?? 0),
      revenue: acc.revenue + (s.revenueGross ?? 0),
    }),
    { clicksQuality: 0, conversions: 0, revenue: 0 },
  );
  const avgCr = totals30d.clicksQuality > 0 ? (totals30d.conversions / totals30d.clicksQuality) * 100 : 0;

  // Quality score per baris = 100 − bot% (clamp 0..100). Bot rate dari AE
  // by-subid; tanpa data bot → null (gauge "—", bukan 100 palsu).
  const qualityScore = (p: (typeof mergedData)[number]): number | null => {
    if (!p.quality || p.quality.clicksTotal === 0) return null;
    return Math.max(0, Math.min(100, 100 - p.quality.botRatePct));
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">Traffic Sources</h1>
          <p className="text-sm text-text-secondary font-mono mt-1">// {mergedData.length} sumber trafik / affiliate yang Anda kelola</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setOpen(true)}>+ ADD SOURCE</Button>
      </div>

      {/* KPI strip 3 kartu (Stitch layar 6) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Panel level={1} bracket={true}>
          <div className="p-3.5">
            <div className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">ACTIVE SOURCES</div>
            <div className="text-2xl font-jetbrains-mono font-semibold numeric text-neon-cyan mt-1">
              {activeSources}
              <span className="text-sm text-text-tertiary font-normal"> / {mergedData.length}</span>
            </div>
          </div>
        </Panel>
        <Panel level={1} bracket={true}>
          <div className="p-3.5">
            <div className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">AVG CONV RATE · 30D</div>
            <div className="text-2xl font-jetbrains-mono font-semibold numeric text-neon-magenta mt-1">
              {avgCr.toFixed(2)}%
            </div>
          </div>
        </Panel>
        <Panel level={1} bracket={true}>
          <div className="p-3.5">
            <div className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">REVENUE · 30D</div>
            <div className="text-2xl font-jetbrains-mono font-semibold numeric text-neon-yellow mt-1">
              {formatCurrency(totals30d.revenue)}
            </div>
          </div>
        </Panel>
      </div>

      {/* Main Panel Container */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 sm:gap-6">
        {/* Left: Data Table */}
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-cyber">
              <thead>
                <tr className="text-[10px] font-display uppercase tracking-wider text-text-secondary border-b border-border-subtle">
                  <th className="py-3 px-4 text-left">SOURCE</th>
                  <th className="py-3 px-4 text-left">SUB_ID</th>
                  <th className="py-3 px-4 text-center">STATUS</th>
                  <th className="py-3 px-4 text-right">PAYOUT RATE</th>
                  <th className="py-3 px-4 text-right" title="100 − bot% dari AE (30D)">QUALITY</th>
                  <th className="py-3 px-4 text-right">CONV 30D</th>
                  <th className="py-3 px-4 text-right">REVENUE 30D</th>
                  <th className="py-3 px-4 text-right">COST 30D</th>
                  <th className="py-3 px-4 text-right">PROFIT 30D</th>
                  <th className="py-3 px-4 text-right">ROI</th>
                  <th className="py-3 px-4 text-right">EPC</th>
                  <th className="py-3 px-4 text-right">AKSI</th>
                </tr>
              </thead>
              <tbody>
                {mergedData.map((p) => {
                  const isExpanded = expandedRow === p.id;
                  const conv = p.stats!.conversionsApproved;
                  const revenue = p.stats!.revenueGross;
                  const clicks = p.stats!.clicksQuality;
                  const totalClicks = p.stats!.clicksTotal;
                  const botRate = totalClicks > 0 ? ((p.quality?.botRatePct ?? 0)).toFixed(1) : '—';

                  return (
                    <>
                      <tr key={p.id} className={`border-t border-border-subtle transition-colors hover:bg-bg-tertiary/50 ${isExpanded ? 'bg-bg-tertiary/80' : ''}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-neon-cyan">{p.displayName || p.subId}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <code className="font-mono text-xs bg-bg-tertiary px-2 py-1 rounded text-neon-cyan">{p.subId}</code>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <StatusBadge status={p.isActive ? 'active' : 'paused'} />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-mono text-sm text-neon-yellow">
                            {formatCurrency(p.payoutValue, p.currency)}
                          </span>
                        </td>
                        {/* Quality gauge mini (Stitch layar 6) — score = 100 − bot% */}
                        <td className="py-3 px-4 text-right">
                          {(() => {
                            const q = qualityScore(p);
                            if (q == null) return <span className="font-mono text-xs text-text-tertiary">—</span>;
                            const barColor =
                              q >= 80 ? 'bg-neon-green' : q >= 60 ? 'bg-neon-yellow' : 'bg-neon-red';
                            const textColor =
                              q >= 80 ? 'text-neon-green' : q >= 60 ? 'text-neon-yellow' : 'text-neon-red';
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-bg-tertiary rounded-full overflow-hidden" title={`quality score ${q.toFixed(0)}/100 (100 − bot ${botRate}%)`}>
                                  <div className={`h-full ${barColor}`} style={{ width: `${q}%` }} />
                                </div>
                                <span className={`font-mono text-xs font-bold ${textColor}`}>{q.toFixed(0)}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {conv}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {formatCurrency(revenue, p.currency)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          <span className="inline-flex items-center justify-end gap-1">
                            {formatCurrency(p.stats?.cost ?? 0)}
                            <button
                              onClick={() =>
                                setCostTarget({
                                  publisher: p,
                                  clicks: p.stats?.clicksQuality ?? 0,
                                  revenue: p.stats?.revenueGross ?? 0,
                                  profitBeforeCost: (p.stats?.revenueGross ?? 0) - (p.stats?.payoutGross ?? 0),
                                })
                              }
                              className="text-text-tertiary hover:text-neon-cyan transition-colors p-1"
                              title="Set cost"
                            >
                              <DollarSign size={14} />
                            </button>
                          </span>
                        </td>
                        <td className={`py-3 px-4 text-right font-mono text-sm ${(p.stats?.profit ?? 0) >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
                          {formatCurrency(p.stats?.profit ?? 0)}
                        </td>
                        <td className={`py-3 px-4 text-right font-mono text-sm ${p.stats?.roiPct == null ? 'text-text-tertiary' : p.stats.roiPct >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
                          {p.stats?.roiPct == null ? '—' : formatPercent(p.stats.roiPct, 1)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-sm">
                          {EPC(revenue, clicks)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<ChevronDown size={14} />}
                              onClick={() => setExpandedRow(isExpanded ? null : p.id)}
                              title="Expand details"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Link2 size={12} />}
                              onClick={() => setLinksFor({ id: p.id, subId: p.subId, campaignIds: p.campaignIds })}
                              title="Get tracking links"
                            >
                              Get Link
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Pencil size={12} />}
                              onClick={() => setEditFor(p)}
                              title="Edit traffic source"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Trash2 size={12} />}
                              onClick={() => handleArchiveOrDelete(p)}
                              title="Delete permanently"
                              className="text-neon-red hover:opacity-70"
                            />
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={12} className="p-0">
                            <div className="p-4 space-y-2 bg-bg-secondary/50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                {/* Payout Rate Editor */}
                                <Panel level={2} bracket={false}>
                                  <div className="p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <DollarSign size={14} className="text-neon-yellow" />
                                      <span className="text-[10px] font-display uppercase tracking-wider text-text-secondary">Payout Rate</span>
                                    </div>
                                    <form
                                      onSubmit={(e) => {
                                        e.preventDefault();
                                        const formData = new FormData(e.currentTarget);
                                        const payoutValue = Number(formData.get('payoutValue'));
                                        const payoutType = formData.get('payoutType') as PublisherRow['payoutType'];
                                        update.mutate({ id: p.id, data: { payoutValue, payoutType } });
                                      }}
                                      className="w-full space-y-2"
                                    >
                                      <Input
                                        label="Value"
                                        name="payoutValue"
                                        type="number"
                                        step="0.01"
                                        defaultValue={p.payoutValue}
                                        className="w-full"
                                      />
                                      <div>
                                        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Type</label>
                                        <select
                                          name="payoutType"
                                          defaultValue={p.payoutType}
                                          className="input-cyber w-full"
                                        >
                                          <option value="cpa">CPA</option>
                                          <option value="cpl">CPL</option>
                                          <option value="revshare">RevShare</option>
                                          <option value="flat">Flat</option>
                                        </select>
                                        <p className="text-[10px] text-text-tertiary mt-1 font-mono">Current: {p.payoutType.toUpperCase()}</p>
                                      </div>
                                      <Button type="submit" size="sm" loading={update.isPending} icon={<Pencil size={14} />}>Save Changes</Button>
                                    </form>
                                  </div>
                                </Panel>

                                {/* Bot Rate Stats */}
                                <Panel level={2} bracket={false}>
                                  <div className="p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-[10px] font-display uppercase tracking-wider text-text-secondary">Quality Snapshot</span>
                                    </div>
                                    <div className="w-full space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-mono text-text-secondary">Bot Rate</span>
                                        <span className={`text-[10px] font-mono font-bold ${Number(botRate) > 30 ? 'text-neon-red' : Number(botRate) > 15 ? 'text-neon-yellow' : 'text-neon-green'}`}>
                                          {botRate}%
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-mono text-text-secondary">Clicks Total</span>
                                        <span className="text-[10px] font-mono text-neon-cyan">{totalClicks}</span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-mono text-text-secondary">Conversions</span>
                                        <span className="text-[10px] font-mono text-neon-green">{conv}</span>
                                      </div>
                                      {p.quality && p.quality.cr > 0 && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-[10px] font-mono text-text-secondary">CR</span>
                                          <span className="text-[10px] font-mono text-neon-purple">{p.quality.cr.toFixed(2)}%</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Panel>
                              </div>

                              {/* Tracking Links Section */}
                              <div className="border-t border-border-subtle pt-3">
                                <TrackingLinksEditor subId={p.subId} campaignIds={p.campaignIds} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {mergedData.length === 0 && !list.isLoading && (
                  <tr>
                    <td colSpan={12} className="text-center py-12 text-text-tertiary">
                      <UserCircleIcon className="mx-auto mb-2" size={32} />
                      <p className="font-mono text-xs">no traffic sources yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Right: Quality Snapshot Panel */}
        <Panel level={2} className="h-fit sticky top-6">
          <div className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-base font-display font-bold uppercase tracking-widest text-neon-cyan">Quality Snapshot</span>
            </div>
            <p className="text-[10px] font-mono text-text-secondary leading-relaxed">
              Real-time quality metrics per source • {bySubId.data?.length ?? 0} tracked
            </p>

            <div className="w-full space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
              {(bySubId.data ?? []).map((row) => (
                <div key={row.subId} className="cyber-panel p-3 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-mono font-bold text-neon-cyan break-all">{row.subId}</span>
                      {row.linkedName && (
                        <span className="text-[9px] font-mono text-text-tertiary">@ {row.linkedName}</span>
                      )}
                    </div>
                    <div className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${row.botRatePct > 30 ? 'bg-neon-red/20 text-neon-red' : row.botRatePct > 15 ? 'bg-neon-yellow/20 text-neon-yellow' : 'bg-neon-green/20 text-neon-green'}`}>
                      {row.botRatePct.toFixed(0)}%
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-mono text-text-tertiary">Bot %</div>
                    </div>
                    <div className="w-24">
                      <Sparkline values={[Math.max(0, 100 - row.botRatePct), Math.max(0, 100 - row.botRatePct * 0.95), Math.max(0, 100 - row.botRatePct * 0.9), row.botRatePct]} height={20} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-1 text-[9px] font-mono border-t border-border-subtle pt-2">
                    <div>
                      <span className="text-text-tertiary block">Conv</span>
                      <span className="text-neon-green font-bold">{row.conversionsApproved}</span>
                    </div>
                    <div>
                      <span className="text-text-tertiary block">Clicks</span>
                      <span className="text-neon-cyan font-bold">{row.clicksTotal}</span>
                    </div>
                    <div>
                      <span className="text-text-tertiary block">Revenue</span>
                      <span className="text-neon-purple font-bold">${row.revenueGross.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {bySubId.isLoading && (
                <div className="text-center py-8 text-text-tertiary">
                  <p className="text-[10px] font-mono">loading quality data...</p>
                </div>
              )}
              {!bySubId.isLoading && !bySubId.data?.length && (
                <div className="text-center py-8 text-text-tertiary">
                  <p className="text-[10px] font-mono">no tracked traffic sources</p>
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* Footer Strip */}
      <p className="text-[10px] font-mono text-text-tertiary text-center border-t border-border-subtle pt-3">
        Sub ID dari klik yang belum terdaftar tetap terlacak otomatis (grup "unregistered") — daftarkan
        sebagai Traffic Source untuk mengatur payout &amp; link.
      </p>

      {/* Add/Edit Modals */}
      <Modal open={open} onClose={() => setOpen(false)} title="New Traffic Source">
        <PublisherForm
          onSubmit={(d: any) => create.mutate(d)}
          onCancel={() => setOpen(false)}
          loading={create.isPending}
        />
      </Modal>

      <Modal
        open={!!editFor}
        onClose={() => setEditFor(null)}
        title={editFor ? `Edit Traffic Source: ${editFor.subId}` : 'Edit Traffic Source'}
      >
        {editFor && (
          <PublisherForm
            initial={editFor}
            onSubmit={(d: any) => update.mutate({ id: editFor.id, data: d })}
            onCancel={() => setEditFor(null)}
            loading={update.isPending}
          />
        )}
      </Modal>

      {linksFor && (
        <TrackingLinksModal
          publisher={linksFor}
          onClose={() => setLinksFor(null)}
        />
      )}

      <SourceCostDrawer
        open={costTarget != null}
        publisher={costTarget?.publisher ?? null}
        clicksInRange={costTarget?.clicks ?? 0}
        revenueInRange={costTarget?.revenue ?? 0}
        profitBeforeCost={costTarget?.profitBeforeCost ?? 0}
        onClose={() => setCostTarget(null)}
        onSaved={() => setCostTarget(null)}
      />
    </div>
  );
}

// ─── Helper Components ──────────────────────────────────────────────────────

interface CampaignOption {
  id: number;
  slug: string;
  displayName: string;
  status: string;
}

function PublisherForm({
  onSubmit,
  onCancel,
  loading,
  initial,
}: {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading: boolean;
  initial?: PublisherRow | null;
}) {
  const isEdit = !!initial;
  const [subId, setSubId] = useState(initial?.subId ?? '');
  const [name, setName] = useState(initial?.displayName ?? '');
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption | null>(null);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const campaigns = useQuery({
    queryKey: ['campaigns-live'],
    queryFn: () => apiClient.get<CampaignOption[]>('/api/v1/campaigns'),
  });
  const liveCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'live');

  const initialCampaignId = initial?.campaignIds?.[0];
  useEffect(() => {
    if (!initialCampaignId || campaigns.data == null) return;
    const found = liveCampaigns.find((c) => c.id === initialCampaignId);
    if (found) setSelectedCampaign(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampaignId, campaigns.data]);

  const filteredCampaigns = (() => {
    const q = campaignSearch.trim().toLowerCase();
    if (!q) return liveCampaigns.slice(0, 8);
    return liveCampaigns
      .filter((c) => c.displayName.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q))
      .slice(0, 8);
  })();

  function pickCampaign(c: CampaignOption) {
    setSelectedCampaign(c);
    setCampaignSearch('');
    setDropdownOpen(false);
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isEdit) {
          onSubmit({
            displayName: name,
            campaignIds: selectedCampaign ? [selectedCampaign.id] : [],
          });
        } else {
          onSubmit({
            subId,
            displayName: name,
            payoutType: 'cpa',
            payoutValue: 0,
            campaignIds: selectedCampaign ? [selectedCampaign.id] : [],
          });
        }
      }}
      className="w-full space-y-4"
    >
      <div className="px-3.5 py-2.5 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed flex gap-2 items-start">
        <Info size={14} className="inline shrink-0 text-neon-cyan mt-0.5" /> Traffic Source = sumber trafik yang Anda beli / affiliate yang menjalankan tracking link. Sub ID akan muncul di klik &amp; conversion.
      </div>
      <Input
        label="Sub ID (slug)"
        value={subId}
        onChange={(e) => setSubId(e.target.value)}
        placeholder="rj-manchester"
        hint={isEdit
          ? 'Sub ID dikunci di mode edit — mengubahnya akan mematikan tracking link yang sudah disebar.'
          : 'ID unik yang dipakai di tracking URL ?sub_id=...'}
        disabled={isEdit}
        required
      />
      <Input
        label="Display Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nama sumber trafik (mis. FB Ads Push ID-7)"
      />

      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          Campaign
        </label>
        {selectedCampaign ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary border border-neon-cyan/40 rounded">
            <span className="text-xs font-mono text-neon-cyan font-bold">{selectedCampaign.displayName}</span>
            <span className="text-[10px] font-mono text-text-tertiary">· {selectedCampaign.slug}</span>
            <button
              type="button"
              onClick={() => setSelectedCampaign(null)}
              className="ml-auto text-text-tertiary hover:text-neon-red text-xs"
              aria-label="Clear campaign selection"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              value={campaignSearch}
              onChange={(e) => {
                setCampaignSearch(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              onBlur={() => {
                setTimeout(() => setDropdownOpen(false), 150);
              }}
              placeholder="Search campaign by name or slug..."
              icon={<Search size={14} />}
            />
            {dropdownOpen && filteredCampaigns.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-bg-secondary border border-border-subtle rounded shadow-lg">
                {filteredCampaigns.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickCampaign(c);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-bg-tertiary text-[11px] font-mono flex items-center gap-2"
                  >
                    <span className="text-text-primary">{c.displayName}</span>
                    <span className="text-text-tertiary">· {c.slug}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" loading={loading} icon={isEdit ? <Pencil size={14} /> : <Plus size={14} />}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Tracking Links Modal ─────────────────────────────────────────────────────

function TrackingLinksModal({
  publisher,
  onClose,
}: {
  publisher: { id: number; subId: string; campaignIds?: number[] };
  onClose: () => void;
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const domains = useQuery({
    queryKey: ['tracker-domains-active'],
    queryFn: () => apiClient.get<{ id: number; hostname: string; status: string; isDefault: boolean }[]>('/api/v1/tracker-domains'),
  });
  const campaigns = useQuery({
    queryKey: ['campaigns-live-tracking'],
    queryFn: () => apiClient.get<CampaignOption[]>('/api/v1/campaigns'),
  });

  const activeDomains = (domains.data ?? []).filter((d) => d.status === 'active' || d.status === 'pending_dns' || d.status === 'pending_ssl');
  const liveCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'live');

  const assignedCampaignId = publisher.campaignIds?.[0];
  const assignedCampaign = assignedCampaignId != null
    ? liveCampaigns.find((c) => c.id === assignedCampaignId) ?? null
    : null;

  const onCopy = (url: string, key: string) => {
    copyToClipboard(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  let links: TrackingLink[] = [];
  if (assignedCampaign) {
    links = activeDomains.map((d) => ({
      key: `${d.id}-${assignedCampaign.id}`,
      domain: d.hostname,
      isDefault: d.isDefault,
      campaignName: assignedCampaign.displayName,
      campaignSlug: assignedCampaign.slug,
      url: `https://${d.hostname}/${assignedCampaign.slug}?sub_id=${encodeURIComponent(publisher.subId)}`,
    }));
  } else {
    const filteredCampaigns = (() => {
      const q = search.trim().toLowerCase();
      if (!q) return liveCampaigns;
      return liveCampaigns.filter((c) =>
        c.displayName.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
      );
    })();
    links = filteredCampaigns.flatMap((c) =>
      activeDomains.map((d) => ({
        key: `${d.id}-${c.id}`,
        domain: d.hostname,
        isDefault: d.isDefault,
        campaignName: c.displayName,
        campaignSlug: c.slug,
        url: `https://${d.hostname}/${c.slug}?sub_id=${encodeURIComponent(publisher.subId)}`,
      })),
    );
  }

  return (
    <Modal open onClose={onClose} title={`Tracking Links: ${publisher.subId}`} size="xl">
      <div className="w-full space-y-2">
        <div className="px-3 py-2 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed flex gap-1.5 items-start">
          <Info size={12} className="inline shrink-0 text-neon-cyan mt-0.5" /> Kasih link berikut ke sub-publisher. Saat mereka kirim traffic ke link ini, klik akan ter-track dengan{' '}
          <code className="text-neon-cyan">sub_id={publisher.subId}</code> dan otomatis di-redirect ke offer sesuai routing rule.
        </div>

        {assignedCampaign ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary border border-neon-cyan/40 rounded">
            <span className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">Campaign:</span>
            <span className="text-xs font-mono text-neon-cyan font-bold">{assignedCampaign.displayName}</span>
            <span className="text-[10px] font-mono text-text-tertiary">· {assignedCampaign.slug}</span>
            <span className="ml-auto text-[10px] font-mono text-text-tertiary">
              {activeDomains.length} domain{activeDomains.length === 1 ? '' : 's'} (failover ready)
            </span>
          </div>
        ) : (
          <>
            <div className="px-3 py-2 border border-neon-yellow/30 bg-neon-yellow/5 rounded text-[10px] font-mono text-text-secondary leading-relaxed flex gap-1.5 items-start">
              <AlertTriangle size={12} className="inline shrink-0 text-neon-yellow mt-0.5" /> Sub-publisher ini belum di-assign ke campaign tertentu — semua campaign × semua domain ditampilkan. Edit publisher untuk pilih campaign spesifik.
            </div>
            <div className="flex-1">
              <Input
                placeholder="Search campaign by name or slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<Search size={14} />}
              />
            </div>
          </>
        )}

        <p className="text-[10px] font-mono text-text-tertiary">
          {assignedCampaign
            ? `Showing ${links.length} link${links.length === 1 ? '' : 's'} for "${assignedCampaign.displayName}" across ${activeDomains.length} domain${activeDomains.length === 1 ? '' : 's'}`
            : `Showing ${links.length} link${links.length === 1 ? '' : 's'}${search ? ` (filtered: "${search}")` : ''}`}
        </p>

        {links.length === 0 ? (
          <div className="cyber-panel p-8 text-center text-text-tertiary">
            <Link2 className="mx-auto mb-2 opacity-50" size={32} />
            <p className="font-mono text-xs">No links available.</p>
          </div>
        ) : (
          <div className="w-full space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {links.map((l) => (
              <div key={l.key} className="cyber-panel p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Link2 size={14} className="text-neon-cyan shrink-0" />
                  <span className="font-display text-xs font-bold">{l.campaignName}</span>
                  <span className="text-[10px] text-text-tertiary font-mono">@ {l.domain}</span>
                  {l.isDefault && (
                    <span className="text-[9px] font-mono text-neon-cyan border border-neon-cyan/40 px-1.5 py-0.5 rounded">DEFAULT</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 text-[11px] font-mono text-neon-cyan bg-bg-tertiary px-2 py-1.5 rounded break-all">
                    {l.url}
                  </code>
                  <Button
                    className="shrink-0"
                    variant={copiedKey === l.key ? 'magenta' : 'neon'}
                    size="sm"
                    icon={copiedKey === l.key ? <CheckCircle size={12} /> : <Copy size={12} />}
                    onClick={() => onCopy(l.url, l.key)}
                  >
                    {copiedKey === l.key ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Inline Tracking Links Editor (inside expanded row) ──────────────────────

function TrackingLinksEditor({ subId, campaignIds }: { subId: string; campaignIds?: number[] }) {
  const [visibleCount, setVisibleCount] = useState(3);

  const campaigns = useQuery({
    queryKey: ['campaigns-live-editor', subId],
    queryFn: () => apiClient.get<CampaignOption[]>('/api/v1/campaigns'),
  });
  const domains = useQuery({
    queryKey: ['tracker-domains-active-editor', subId],
    queryFn: () => apiClient.get<{ id: number; hostname: string; status: string; isDefault: boolean }[]>('/api/v1/tracker-domains'),
  });

  const liveCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'live');
  const activeDomains = (domains.data ?? []).filter((d) => d.status === 'active' || d.status === 'pending_dns' || d.status === 'pending_ssl');

  const assignedCampaignId = campaignIds?.[0];
  const assignedCampaign = assignedCampaignId != null
    ? liveCampaigns.find((c) => c.id === assignedCampaignId) ?? null
    : null;

  let links: TrackingLink[] = [];
  if (assignedCampaign) {
    links = activeDomains.map((d) => ({
      key: `${d.id}-${assignedCampaign.id}`,
      domain: d.hostname,
      isDefault: d.isDefault,
      campaignName: assignedCampaign.displayName,
      campaignSlug: assignedCampaign.slug,
      url: `https://${d.hostname}/${assignedCampaign.slug}?sub_id=${encodeURIComponent(subId)}`,
    }));
  } else {
    links = liveCampaigns.flatMap((c) =>
      activeDomains.map((d) => ({
        key: `${d.id}-${c.id}`,
        domain: d.hostname,
        isDefault: d.isDefault,
        campaignName: c.displayName,
        campaignSlug: c.slug,
        url: `https://${d.hostname}/${c.slug}?sub_id=${encodeURIComponent(subId)}`,
      })),
    );
  }

  const displayedLinks = links.slice(0, visibleCount);

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-display uppercase tracking-wider text-text-secondary">Active Tracking Links</span>
        <span className="text-[10px] font-mono text-text-tertiary">{links.length} total</span>
      </div>

      <div className="w-full space-y-2">
        {displayedLinks.map((l) => (
          <div key={l.key} className="flex items-center gap-2 cybersmall-panel p-2">
            <Link2 size={12} className="text-neon-cyan shrink-0" />
            <span className="text-[10px] font-mono text-neon-cyan truncate flex-1">{l.url}</span>
          <Button
            variant="ghost"
            size="sm"
            icon={l.isDefault ? <CheckCircle size={10} /> : <Copy size={10} />}
            onClick={() => copyToClipboard(l.url)}
            title="Copy link"
          />
          </div>
        ))}
        {links.length > visibleCount && (
          <Button variant="ghost" size="sm" onClick={() => setVisibleCount(visibleCount + 3)} className="text-[10px]">
            Show {Math.min(visibleCount + 3, links.length)} more
          </Button>
        )}
      </div>
    </div>
  );
}

// Simple User Circle Icon fallback (Lucide doesn't export it as default)
function UserCircleIcon(props: any) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 32}
      height={props.size || 32}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6a4 4 0 0 0 8 0V6" />
    </svg>
  );
}
